import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import apiClient from "@/api/client";
import { Job } from "@/api/Services/joblist";
import { Upload } from "lucide-react";
import { uploadResume, submitApplicant } from "@/api/Services/applicant";
import { INDIAN_STATES, EXPERIENCE_OPTIONS } from "./consts";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { getLocations } from "@/api/Services/locations";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

const SpecificeFeaturedJobs = () => {
  const { id } = useParams<{ id: string }>(); // Get job _id from URL
  const [job, setJob] = useState<Job | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [locations, setLocations] = useState<string[]>([]);


  // ✅ Captcha states
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState("");
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});


  const validateForm = () => {
  const newErrors: { [key: string]: string } = {};

  if (!formData.applicantName.trim()) {
    newErrors.applicantName = "Name is required";
  }

  if (!formData.email.trim()) {
    newErrors.email = "Email is required";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    newErrors.email = "Enter a valid email address";
  }

  if (!formData.phone.trim()) {
    newErrors.phone = "Phone number is required";
  } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
    newErrors.phone = "Enter a valid 10-digit Indian mobile number";
  }

  // if (!formData.state) {
  //   newErrors.state = "Please select a state";
  // }

  if (!formData.location) {
    newErrors.location = "Please select a location";
  }

  if (!formData.experience) {
    newErrors.experience = "Please select experience";
  }

  if (!formData.websiteLink) {
    newErrors.experience = "Please Add WebsiteLink";
  }

  if (!formData.linkedinLink) {
    newErrors.experience = "Please Add Linkedin Link";
  }

  if (!resumeFile) {
    newErrors.resumeFile = "Resume is required";
  } else if (!/\.(pdf|doc|docx)$/i.test(resumeFile.name)) {
    newErrors.resumeFile = "Resume must be a PDF or Word document";
  } else if (resumeFile.size > 2 * 1024 * 1024) { // 2MB limit
    newErrors.resumeFile = "File size must be less than 2MB";
  }

  if (!captchaInput.trim()) {
    newErrors.captcha = "Captcha is required";
  } else if (captchaInput !== captcha) {
    newErrors.captcha = "Captcha is incorrect";
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};


  // Add states for form fields
    const [formData, setFormData] = useState({
      applicantName: "",
      email: "",
      phone: "",
      location: "",
      experience: "",
      linkedinLink: "",
      websiteLink: "",
      region: "India", // default if needed
      coverLetter: "cover letter",
    });
    const [resumeFile, setResumeFile] = useState<File | null>(null);

    const resetForm = () => {
      setFormData({
        applicantName: "",
        email: "",
        phone: "",
        location: "",
        experience: "",
        linkedinLink: "",
        websiteLink: "",
        region: "India",
        coverLetter: "cover letter",
      });
      setResumeFile(null);
      setCaptchaInput("");
      setErrors({}); 
      refreshCaptcha();
};


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
      const fetchLocations = async () => {
        const data = await getLocations();
        setLocations(data); // API already returns array
      };
      fetchLocations();
    }, []);




  function generateCaptcha() {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  // 🎨 Draw captcha on canvas
  const drawCaptcha = (text: string) => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Background
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, randomColor());
  gradient.addColorStop(1, randomColor());
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.font = "bold 28px sans-serif";
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";

  // Dynamically center + shift left
  const charSpacing = 20;
  const textWidth = text.length * charSpacing;
  const xStart = (canvas.width - textWidth) / 2 - 10; // move 10px left

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const angle = (Math.random() - 0.5) * 0.5;
    ctx.save();
    ctx.translate(xStart + i * charSpacing, canvas.height / 2);
    ctx.rotate(angle);
    ctx.fillStyle = randomColor();
    ctx.fillText(char, 0, 0);
    ctx.restore();
  }

  // Noise lines
  for (let i = 0; i < 5; i++) {
    ctx.strokeStyle = randomColor();
    ctx.beginPath();
    ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
    ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
    ctx.stroke();
  }
};



  function randomColor() {
    return `rgb(${Math.floor(Math.random() * 256)},${Math.floor(
      Math.random() * 256
    )},${Math.floor(Math.random() * 256)})`;
  }

  const refreshCaptcha = () => {
    const newCaptcha = generateCaptcha();
    setCaptcha(newCaptcha);
    setCaptchaInput("");
    setTimeout(() => drawCaptcha(newCaptcha), 0);
  };

  // ✅ When modal opens, generate captcha
useEffect(() => {
  if (isOpen) {
    refreshCaptcha();
  }
}, [isOpen]);

useEffect(() => {
  if (!isOpen) {
    resetForm();
  }
}, [isOpen]);



  

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (!validateForm()) return; // stop if invalid

  try {
    let resumeFileId = "";
    if (resumeFile) {
      resumeFileId = await uploadResume(resumeFile);
    }

    const payload = {
      ...formData,
      jobId: id || "",
      resumeFile: resumeFileId,
    };

    console.log("Final Payload:", payload);
    await submitApplicant(payload);

    setIsOpen(false);
    setIsSuccess(true);
  } catch (error) {
    console.error("Application submission failed:", error);
    alert("Something went wrong while applying.");
  }
};




  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await apiClient.get(`${API_BASE_URL}/intron/job/job/${id}`);
        setJob(res.data?.data || null);
      } catch (err) {
        console.error("Error fetching job details:", err);
      }
    };

    if (id) fetchJob();
  }, [id]);

  if (!job) {
    return <p className="text-center mt-10">Loading job details...</p>;
  }

  return (
    <section className="bg-gray-50 min-h-screen flex flex-col">
      {/* Header Section */}
      <div className="bg-[#1F3342] text-white py-16 px-6 text-center">
        <h1 className="text-3xl md:text-4xl font-bold">
          {job.jobName}{" "}
          <span className="text-gray-300 text-lg font-normal">
            ({job.jobId})
          </span>
        </h1>

        {/* Breadcrumbs */}
        <p className="mt-2 text-sm">
          <Link to="/" className="text-primary hover:underline">
            Home
          </Link>{" "}
          /{" "}
          <Link to="/career" className="text-primary hover:underline">
            Careers
          </Link>{" "}
          / {job.jobName}
        </p>

        <button
          onClick={() => setIsOpen(true)}
          className="mt-6 bg-primary hover:bg-[#e65a2a] px-6 py-2 rounded-full font-semibold cursor-pointer"
        >
          Apply Now
        </button>
      </div>

      {/* Content Section */}
      <div className="max-w-4xl mx-auto ml-0 lg:ml-[320px] px-6 py-12 -mt-0 rounded-lg">
        {/* Role & Location */}
        <div className="mb-6">
          <p>
            <span className="font-semibold">Role</span> – {job.jobName}
          </p>
          <p>
            <span className="font-semibold">Location</span> –{" "}
            {job.location.join(", ")}
          </p>
        </div>

        {/* Job Description */}
        <div className="mb-8">
          <h2 className="font-semibold text-lg mb-2">Job Description</h2>
          <p
            className="text-gray-700 mb-4 text-justify"
            dangerouslySetInnerHTML={{
              __html: job.description.replace(/\n/g, "<br />"),
            }}
          />
        </div>

        {/* Skills Required */}
        <div className="mb-8">
          <h2 className="font-semibold text-lg mb-2">Skills Required</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-1 pl-4">
            {job.requiredSkills.map((skill) => (
              <li key={skill}>{skill}</li>
            ))}
          </ul>
        </div>

        {/* Experience Required */}
        <div className="mb-8">
          <h2 className="font-semibold text-lg mb-2">Experience Required</h2>
          <ul className="list-disc list-inside text-gray-700 space-y-2 pl-4">
            {job.experienceRequired.map((exp, idx) => (
              <li key={idx}>{exp}</li>
            ))}
          </ul>
        </div>

        {/* Bottom Apply Button */}
        
      </div>
      <div className="text-center mx-auto mb-40">
          <button
            onClick={() => setIsOpen(true)}
            className="bg-primary hover:bg-[#e65a2a] px-6 py-2 rounded-full font-semibold text-white cursor-pointer"
          >
            Apply Now
          </button>
      </div>
      {/* Apply Modal */}
      {isOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
    <div className="bg-white rounded-xl shadow-lg w-[95%] sm:w-[90%] md:w-full max-w-3xl relative p-4 sm:p-6 md:p-8 max-h-[90vh] overflow-y-auto">
      {/* Close Button */}
      <button
        className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-500 hover:text-black text-3xl sm:text-4xl cursor-pointer"
        onClick={() => setIsOpen(false)}
      >
        &times;
      </button>

      <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
        Apply Job - <span className="text-primary">{job.jobName}</span>
      </h2>

      <form
        className="grid grid-cols-1 md:grid-cols-2 gap-2"
        onSubmit={handleSubmit}
      >
        {/* Name */}
        <div>
          <label htmlFor="applicantName" className="block text-sm font-medium text-gray-700 mb-1 mt-1">
            Full Name
          </label>
          <input
            type="text"
            id="applicantName"
            name="applicantName"
            value={formData.applicantName}
            onChange={handleChange}
            placeholder="Enter Name"
            className="border rounded-full px-4 py-2 w-full"
          />
          {errors.applicantName && (
            <p className="text-red-500 text-sm mt-1">{errors.applicantName}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter Email ID"
            className="border rounded-full px-4 py-2 w-full"
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
          )}
        </div>

        {/* Mobile with Flag */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Mobile Number
          </label>
          <div className="flex w-full">
            <span className="inline-flex items-center gap-2 px-3 rounded-l-full border border-r-0 border-gray-300 bg-gray-100 text-gray-600">
              <img
                src="https://flagcdn.com/w20/in.png"
                alt="India Flag"
                className="w-5 h-3 object-cover"
              />
              +91
            </span>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter Mobile No."
              className="border rounded-r-full px-4 py-2 w-full"
            />
          </div>
          {errors.phone && (
            <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
          )}
        </div>

        {/* State Dropdown */}
        {/* <div>
          <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
            State
          </label>
          <Select
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, state: value }))
            }
            value={formData.state}
          >
            <SelectTrigger id="state" className="w-full border rounded-full px-4 py-2 h-11">
              <SelectValue placeholder="Select State" />
            </SelectTrigger>
            <SelectContent>
              {INDIAN_STATES.map((st, idx) => (
                <SelectItem key={idx} value={st}>
                  {st}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.state && (
            <p className="text-red-500 text-sm mt-1">{errors.state}</p>
          )}
        </div> */}

        {/* Location Dropdown */}
        <div>
          <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
            Location
          </label>
          <Select
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, location: value }))
            }
            value={formData.location}
          >
            <SelectTrigger id="location" className="w-full border rounded-full px-4 py-2 h-11 cursor-pointer">
              <SelectValue placeholder="Select Location" />
            </SelectTrigger>
            <SelectContent>
              {locations.length > 0 ? (
                locations.map((loc, idx) => (
                  <SelectItem key={idx} value={loc}>
                    {loc}
                  </SelectItem>
                ))
              ) : (
                <SelectItem disabled value="loading">
                  Loading...
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          {errors.location && (
            <p className="text-red-500 text-sm mt-1">{errors.location}</p>
          )}
        </div>

        {/* Experience Dropdown */}
        <div>
          <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
            Years of Experience
          </label>
          <Select
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, experience: value }))
            }
            value={formData.experience}
          >
            <SelectTrigger id="experience" className="w-full border rounded-full px-4 py-2 h-11">
              <SelectValue placeholder="Select Experience" />
            </SelectTrigger>
            <SelectContent>
              {EXPERIENCE_OPTIONS.map((exp, idx) => (
                <SelectItem key={idx} value={exp}>
                  {exp}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.experience && (
            <p className="text-red-500 text-sm mt-1">{errors.experience}</p>
          )}
        </div>

        {/* LinkedIn */}
        <div>
          <label htmlFor="linkedinLink" className="block text-sm font-medium text-gray-700 mb-1">
            LinkedIn Profile
          </label>
          <input
            type="url"
            id="linkedinLink"
            name="linkedinLink"
            value={formData.linkedinLink}
            onChange={handleChange}
            placeholder="LinkedIn Profile Link"
            className="border rounded-full px-4 py-2 w-full"
          />
          {errors.linkedinLink && (
            <p className="text-red-500 text-sm mt-1">{errors.linkedinLink}</p>
          )}
        </div>

        {/* Website */}
        <div>
          <label htmlFor="websiteLink" className="block text-sm font-medium text-gray-700 mb-1">
            Website
          </label>
          <input
            type="url"
            id="websiteLink"
            name="websiteLink"
            value={formData.websiteLink}
            onChange={handleChange}
            placeholder="Website Profile Link"
            className="border rounded-full px-4 py-2 w-full"
          />
          {errors.websiteLink && (
            <p className="text-red-500 text-sm mt-1">{errors.websiteLink}</p>
          )}
        </div>

        {/* File Upload */}
        <div className="relative col-span-1 md:col-span-1">
          <label htmlFor="resume-upload" className="block text-sm font-medium text-gray-700 mb-1">
            Resume
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
            className="hidden"
            id="resume-upload"
          />
          <label
            htmlFor="resume-upload"
            className="border rounded-full px-4 py-2 w-full cursor-pointer flex items-center justify-between"
          >
            {resumeFile ? resumeFile.name : "Upload Resume"}
            <Upload className="text-gray-500 w-5 h-5" />
          </label>
          {errors.resumeFile && (
            <p className="text-red-500 text-sm mt-1">{errors.resumeFile}</p>
          )}
        </div>

        {/* Captcha */}
        <div className="col-span-1 md:col-span-2">
          <label htmlFor="captcha" className="block text-sm font-medium text-gray-700 mb-1">
            CAPTCHA
          </label>
          <div className="flex flex-wrap sm:flex-nowrap items-center space-x-2">
            <canvas
              ref={canvasRef}
              width={180}
              height={50}
              className="border rounded-lg"
            />
            <button
              type="button"
              onClick={refreshCaptcha}
              className="px-3 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
            >
              ↻
            </button>
            <input
              type="text"
              id="captcha"
              value={captchaInput}
              onChange={(e) => setCaptchaInput(e.target.value)}
              placeholder="Enter Captcha"
              className="border rounded-full px-4 py-2 flex-1 mt-2 sm:mt-0"
            />
          </div>
          {errors.captcha && (
            <p className="text-red-500 text-sm mt-1">{errors.captcha}</p>
          )}
        </div>

        {/* Submit */}
        <div className="col-span-1 md:col-span-2 text-end mt-4">
          <button
            type="submit"
            className="bg-primary hover:bg-orange-600 text-white px-6 py-2 rounded-full font-semibold cursor-pointer"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  </div>
)}



      {/* ✅ Success Modal */}
      {isSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-8 text-center relative">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-black text-4xl cursor-pointer"
              onClick={() => setIsSuccess(false)}
            >
              &times;
            </button>

            <div className="flex justify-center mb-4">
              <div className="w-15 h-15 bg-primary text-white rounded-full flex items-center justify-center">
                <span className="text-3xl">✔</span>
              </div>
            </div>

            <h2 className="text-xl font-bold mb-2">
              Thank You for Your Application
            </h2>
            <p className="text-gray-600 mb-4">
              Your application has been received successfully. Our recruitment
              team will review your profile and contact you if your
              qualifications match our requirements.
            </p>
            <p className="text-gray-800 font-semibold">
              We appreciate your interest in building a career with{" "}
              <span className="text-primary">Intron Life Sciences.</span>
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default SpecificeFeaturedJobs;
